import {
  AiInterviewerFragment,
  AiInterviewerVideoMode,
  AiInterviewQuestion,
  AiInterviewQuestionType,
} from "../../graphql";
import { replaceCompanyNameWildcard } from "./EmailBodyEditor/utils";
import {
  AiInterviewerFormValues,
  AiInterviewerVideoSetting,
  DEFAULT_INVITATION_SMS_MESSAGE,
} from "./types";

const toVideoSetting = (
  videoMode: string | null | undefined
): AiInterviewerVideoSetting => {
  if (videoMode === AiInterviewerVideoMode.Disabled) {
    return AiInterviewerVideoMode.Disabled;
  }
  if (videoMode === AiInterviewerVideoMode.Optional) {
    return AiInterviewerVideoMode.Optional;
  }
  if (videoMode === AiInterviewerVideoMode.Required) {
    return AiInterviewerVideoMode.Required;
  }
  return AiInterviewerVideoMode.Disabled;
};

/** Use empty strings for any missing values so react hook's `isDirty` check is more consistent */
export const getFormDefaults = (
  { name, displayName, currentVersion, position }: AiInterviewerFragment,
  companyName?: string
): AiInterviewerFormValues => {
  const getQuestionDefault = (
    question: AiInterviewQuestion
  ): AiInterviewQuestion => {
    const { rubric, qualificationCriteria, summaryCriteria } = question;

    return {
      ...question,
      customWeight: question.customWeight ?? undefined,
      qualificationCriteria: qualificationCriteria ?? "",
      summaryCriteria: summaryCriteria ?? "",
      rubric: rubric.length === 4 ? rubric : Array(4).fill(""),
    };
  };

  return {
    name,
    displayName: displayName ?? "",
    positionId: position?.id,
    questions: currentVersion.questions.map(getQuestionDefault),
    inviteExpirationDays: currentVersion.inviteExpirationDays,
    reminderDays: currentVersion.reminderDays,
    reminderEmailEnabled: currentVersion.reminderEmailEnabled ?? false,
    completionEmailEnabled: currentVersion.completionEmailEnabled ?? false,
    positionInfo: currentVersion.positionInfo || "",
    introduction: currentVersion.introduction || "",
    closing: currentVersion.closing || "",
    jobDescription: currentVersion.jobDescription || "",
    verbatim: currentVersion.verbatim ? "true" : "false",
    customQuestionWeightsEnabled:
      currentVersion.customQuestionWeightsEnabled ?? false,
    videoSetting: toVideoSetting(currentVersion.videoMode),
    emailConfig: {
      invitation: {
        fromName: currentVersion.invitationEmailConfig.fromName || "",
        fromEmail: currentVersion.invitationEmailConfig.fromEmail || "",
        replyTo: currentVersion.invitationEmailConfig.replyTo || "",
        subject: companyName
          ? replaceCompanyNameWildcard(
              currentVersion.invitationEmailConfig.subject || "",
              companyName
            )
          : currentVersion.invitationEmailConfig.subject || "",
        bodyHtml: currentVersion.invitationEmailConfig.bodyHtml || "",
      },
      reminder: {
        fromName: currentVersion.reminderEmailConfig.fromName || "",
        fromEmail: currentVersion.reminderEmailConfig.fromEmail || "",
        replyTo: currentVersion.reminderEmailConfig.replyTo || "",
        subject: currentVersion.reminderEmailConfig.subject || "",
        bodyHtml: currentVersion.reminderEmailConfig.bodyHtml || "",
      },
      completion: {
        fromName: currentVersion.completionEmailConfig.fromName || "",
        fromEmail: currentVersion.completionEmailConfig.fromEmail || "",
        replyTo: currentVersion.completionEmailConfig.replyTo || "",
        subject: currentVersion.completionEmailConfig.subject || "",
        bodyHtml: currentVersion.completionEmailConfig.bodyHtml || "",
      },
    },
    invitationSmsEnabled: currentVersion.invitationSmsEnabled ?? false,
    invitationSmsMessage:
      currentVersion.invitationSmsMessage || DEFAULT_INVITATION_SMS_MESSAGE,
    completionNotificationEmails:
      currentVersion.completionNotificationEmails ?? [],
  };
};

/** Set non-relevant question types to be null before sending to the backend  */
export const processFormData = (
  data: AiInterviewerFormValues
): AiInterviewerFormValues => ({
  ...data,
  questions: data.questions.map((question) => {
    const result = {
      ...question,
      followups: (question.followups ?? [])
        .map((followup) =>
          typeof followup === "string" ? followup.trim() : ""
        )
        .filter((followup) => followup.length > 0),
    };
    if (question.type === AiInterviewQuestionType.Score) {
      result.qualificationCriteria = null;
      result.summaryCriteria = null;
    } else if (question.type === AiInterviewQuestionType.Qualification) {
      result.rubric = [];
      result.summaryCriteria = null;
      result.customWeight = null;
    } else if (question.type === AiInterviewQuestionType.Summary) {
      result.rubric = [];
      result.qualificationCriteria = null;
      result.customWeight = null;
    }
    return result;
  }),
});

// Compliance check utilities

export const COMPLIANCE_CHECK_DEBOUNCE_MS = 500;

/** Question shape needed for getCriteria */
export type QuestionWithCriteria = {
  type?: AiInterviewQuestionType | null;
  qualificationCriteria?: string | null;
  summaryCriteria?: string | null;
  rubric?: (string | null)[] | null;
};

/**
 * Gets the criteria string based on question type.
 */
export const getCriteria = (question: QuestionWithCriteria): string => {
  switch (question.type) {
    case AiInterviewQuestionType.Qualification:
      return question.qualificationCriteria?.trim() ?? "";
    case AiInterviewQuestionType.Summary:
      return question.summaryCriteria?.trim() ?? "";
    case AiInterviewQuestionType.Score:
      return question.rubric?.filter(Boolean).join("\n") ?? "";
    default:
      return "";
  }
};

/**
 * Builds a string key representing the current question content for change detection.
 */
export const buildContentKey = (
  question: string,
  followups: string[],
  criteria: string
): string => {
  return `${question}||${followups.filter(Boolean).join("||")}||${criteria}`;
};

// Publish warnings

export interface PublishWarning {
  field: string;
  label: string;
  reason: "missing" | "short";
}

const SHORT_FIELD_MIN_LENGTH = 20;
const JOB_DESCRIPTION_MIN_LENGTH = 200;

export const getPublishWarnings = (
  data: AiInterviewerFormValues
): PublishWarning[] => {
  const warnings: PublishWarning[] = [];

  const checkContent = (
    value: string | undefined | null,
    field: string,
    label: string,
    minLength: number = SHORT_FIELD_MIN_LENGTH
  ): void => {
    const trimmed = (value ?? "").trim();
    if (!trimmed) {
      warnings.push({ field, label, reason: "missing" });
    } else if (trimmed.length < minLength) {
      warnings.push({ field, label, reason: "short" });
    }
  };

  const checkPresence = (
    value: string | undefined | null,
    field: string,
    label: string
  ): void => {
    if (!(value ?? "").trim()) {
      warnings.push({ field, label, reason: "missing" });
    }
  };

  checkContent(
    data.jobDescription,
    "jobDescription",
    "Job description",
    JOB_DESCRIPTION_MIN_LENGTH
  );
  checkContent(data.positionInfo, "positionInfo", "Org & position info");
  checkContent(data.introduction, "introduction", "Opening script");
  checkContent(data.closing, "closing", "Closing script");
  checkPresence(
    data.emailConfig.invitation.fromName,
    "emailConfig.invitation.fromName",
    "From name (invitation email)"
  );
  checkPresence(
    data.emailConfig.invitation.replyTo,
    "emailConfig.invitation.replyTo",
    "Reply-to (invitation email)"
  );

  return warnings;
};
